import React from 'react';
import { useNotificationContext } from '../AuthContext/NotificationContext';
import { BaseNotification }       from '../../notifications/components/BaseNotification';

/**
 * Renderer de notificaciones.
 *
 * - Renderiza el componente registrado en el registry para cada tipo.
 * - Usa BaseNotification como fallback si no hay componente específico.
 * - Muestra un badge "N en cola" cuando el queue manager tiene pendientes.
 * - El contenedor tiene aria-live="polite" para screen readers.
 */
const NotificationToast = () => {
  const { notifications, queuedCount, clearNotifications, removeNotification } =
    useNotificationContext();

  return (
    <>
      <style>{`
        @keyframes notifEnter {
          0%   { transform: translateX(110%) scale(0.95); opacity: 0; }
          60%  { transform: translateX(-4%) scale(1.01); opacity: 1; }
          100% { transform: translateX(0)   scale(1);    opacity: 1; }
        }
        @keyframes notifExit {
          0%   { transform: translateX(0)    scale(1);    opacity: 1; }
          100% { transform: translateX(110%) scale(0.95); opacity: 0; }
        }
        @keyframes shrinkProgress {
          from { width: 100%; }
          to   { width: 0%;   }
        }

        .notification-enter {
          animation: notifEnter 0.42s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        .notification-exit {
          animation: notifExit 0.28s ease-in forwards;
        }
      `}</style>

      <div
        aria-live="polite"
        aria-label="Notificaciones del sistema"
        aria-relevant="additions removals"
        className="fixed top-24 right-4 z-50 flex flex-col gap-2.5 pointer-events-none w-[22rem] max-w-[calc(100vw-2rem)]"
      >
        {notifications.map((notif) => {
          const Component = notif.config?.component ?? BaseNotification;
          return (
            <div key={notif.id} className="pointer-events-auto">
              <Component notification={notif} onDismiss={removeNotification} />
            </div>
          );
        })}

        {/* Badge de cola: aparece cuando hay notificaciones esperando */}
        {queuedCount > 0 && (
          <div className="pointer-events-auto notification-enter">
            <button
              onClick={clearNotifications}
              className="
                w-full flex items-center justify-between gap-2 px-3.5 py-2.5
                rounded-xl bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl
                border border-gray-200/60 dark:border-zinc-700/50
                shadow-[0_4px_16px_rgba(0,0,0,0.08)]
                text-xs font-medium text-gray-500 dark:text-gray-400
                hover:text-gray-800 dark:hover:text-gray-200
                hover:border-gray-300 dark:hover:border-zinc-600
                transition-all duration-150 group
              "
              aria-label={`${queuedCount} notificaciones en cola. Clic para limpiar todas.`}
            >
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1">
                  {[...Array(Math.min(3, queuedCount))].map((_, i) => (
                    <div
                      key={i}
                      className="w-2 h-2 rounded-full bg-violet-400 dark:bg-violet-500 ring-1 ring-white dark:ring-zinc-900"
                      style={{ opacity: 1 - i * 0.25 }}
                    />
                  ))}
                </div>
                <span>
                  {queuedCount} notificación{queuedCount !== 1 ? 'es' : ''} en cola
                </span>
              </div>
              <span className="text-[10px] text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
                Limpiar
              </span>
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationToast;
